import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Box, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { ProviderNewClient } from '@uplift/api';
import { patientIntakeFormRoute, providerPatientRoute } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { getCareTypeLabel, mapAssessmentScoreToDescription, toKebabCase } from '@uplift/utils';

import { LineItem, LineItemProps } from './line-item';
import { useClientFormProgressStyles } from './styles';
import { SubSystem } from '@uplift/types';

export type ClientFormProgressListProps = {
  appointmentAssessments: {
    submissionId: string;
    formId: string;
    formTitle: string;
    score: number;
    scoreDiffPercentage: number;
    completedAt: string;
    requiresAttention?: boolean;
  }[];
  appointmentIntakeAssessment: {
    createdAt: string;
    submissionId: string;
    highRisk: boolean;
  };
  patient: ProviderNewClient;
  isLoading: boolean;
  phq9Id?: string;
  gad7Id?: string;
};

export const ClientFormProgressList: React.FC<ClientFormProgressListProps> = ({
  appointmentAssessments,
  appointmentIntakeAssessment,
  patient,
  isLoading,
  phq9Id,
  gad7Id,
}) => {
  const l10n = useL10n();
  const { classes } = useClientFormProgressStyles();
  const phq9Desc = mapAssessmentScoreToDescription('phq9', l10n);
  const gad7Desc = mapAssessmentScoreToDescription('gad7', l10n);

  const patientId = patient.userId;
  const isReferral = !!patient.referral;
  const isPsychReferral = patient.referral?.fromUser?.subsystem !== SubSystem.higherEd;

  const items: LineItemProps[] = useMemo(() => {

    const referral = {
      label: l10n.dashboard.formProgress.referral(getCareTypeLabel(patient?.referral?.careType)),
      referral: true,
      referralContent: patient?.referral?.content,
      referralFromUser: patient?.referral?.fromUser.fullName,
      to: (isPsychReferral
        ? providerPatientRoute.createLink({ patientId }, { activityType: 'match', activityId: patient?.referral?.fromUser.userId })
        : providerPatientRoute.createLink({ patientId }, { activityType: 'referral', activityId: patientId })
      )
    };

    const intakeNeedsAttention = Boolean(appointmentIntakeAssessment?.highRisk);

    const intake = {
      label: l10n.dashboard.formProgress.intake,
      to: (
        appointmentIntakeAssessment
          ? patientIntakeFormRoute.createLink({ patientId, submissionId: appointmentIntakeAssessment.submissionId })
          : ''
      ),
      attention: intakeNeedsAttention,
      completed: !!appointmentIntakeAssessment,
    };

    const phq9Form = appointmentAssessments.find(a => a.formId === phq9Id);

    const phq9 = {
      label: l10n.dashboard.formProgress.phq9,
      to: providerPatientRoute.createLink(
        { patientId },
        { activityType: 'assessment', activityId: phq9Form?.submissionId }
      ),
      completed: !!phq9Form,
      attention: phq9Form?.requiresAttention,
      score: phq9Form?.score,
      description: phq9Form?.score ? phq9Desc(phq9Form.score) : undefined,
    };

    const gad7Form = appointmentAssessments.find(a => a.formId === gad7Id);

    const gad7 = {
      label: l10n.dashboard.formProgress.gad7,
      to: providerPatientRoute.createLink(
        { patientId },
        { activityType: 'assessment', activityId: gad7Form?.submissionId }
      ),
      completed: !!gad7Form,
      attention: gad7Form?.requiresAttention,
      score: gad7Form?.score,
      description: gad7Form?.score ? gad7Desc(gad7Form.score) : undefined,
    };

    const commonForms = [ intake, phq9, gad7 ];
    const studentForms = [ referral, ...commonForms ];

    return isReferral ? studentForms : commonForms;
  }, [ appointmentAssessments, appointmentIntakeAssessment, gad7Desc, gad7Id, isPsychReferral, isReferral, l10n.dashboard.formProgress, patient?.referral?.careType, patient?.referral?.content, patient?.referral?.fromUser.fullName, patient?.referral?.fromUser.userId, patientId, phq9Desc, phq9Id ]);

  return (
    <Box width="100%">
      <List disablePadding>
        {items.map((item) => {
          if (isLoading) {
            return (
              <ListItem divider key={item.label}>
                <ListItemText
                  primary={item.label} className={classes.itemText} slotProps={{
                    primary: { variant: 'body2', color: 'textPrimary' }
                  }}
                />
              </ListItem>
            );
          }

          return (
            <ListItemButton
              divider
              key={item.label}
              disabled={!item.completed && !item.referral}
              component={Link}
              to={item.to}
              data-testid={`${toKebabCase(item.label)}-form`}
            >
              <ListItemText primary={<LineItem {...item} />} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};
